import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'rgb(239, 231, 221)',
        display: 'flex',
        alignItems: 'center',
        py: 3
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: isMobile ? '90vh' : '80vh',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              position: 'sticky',
              top: 0,
              backgroundColor: 'inherit',
              zIndex: 10,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
            }}
          >
            <Typography variant="h6" component="h1">
              Privacy Policy
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Effective Date: June 9, 2025
            </Typography>
          </Box>

          {/* Scrollable Content */}
          <Box
            sx={{
              overflowY: 'auto',
              px: 2,
              pb: 2,
              flex: 1
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              1. Introduction
            </Typography>
            <Typography variant="body2" paragraph>
              This Privacy Policy describes how Landnest collects, uses, and protects your personal information...
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              2. Information We Collect
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Personal Information:"
                  secondary="Name, email address, phone number, address, etc."
                  secondaryTypographyProps={{ component: 'span' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Property Details:"
                  secondary="Listings, preferences, and inquiry data."
                  secondaryTypographyProps={{ component: 'span' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Device & Usage Data:"
                  secondary="IP address, browser type, device info, and usage activity."
                  secondaryTypographyProps={{ component: 'span' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Documents:"
                  secondary="KYC documents, property documents, and proof of identity if uploaded."
                  secondaryTypographyProps={{ component: 'span' }}
                />
              </ListItem>
            </List>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              3. How We Use Your Information
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="To facilitate transactions..." />
              </ListItem>
              <ListItem>
                <ListItemText primary="To improve user experience..." />
              </ListItem>
              <ListItem>
                <ListItemText primary="To send updates, alerts, and promotional messages..." />
              </ListItem>
              <ListItem>
                <ListItemText primary="To comply with legal obligations..." />
              </ListItem>
            </List>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              4. Data Sharing
            </Typography>
            <Typography variant="body2" paragraph>
              We may share your information with:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Agents, brokers, and builders..." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Third-party service providers..." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Regulatory authorities..." />
              </ListItem>
            </List>
            <Typography variant="body2" paragraph>
              We do not sell your data to third parties for profit.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              5. Third-Party Logins
            </Typography>
            <Typography variant="body2" paragraph>
              If you log in using Google or Facebook...
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              6. Data Security
            </Typography>
            <Typography variant="body2" paragraph>
              We implement standard security practices...
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              7. Your Rights
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Access your data..." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Request correction..." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Opt out of marketing..." />
              </ListItem>
            </List>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              8. Data Retention
            </Typography>
            <Typography variant="body2" paragraph>
              We retain your information as long as your account is active...
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              9. Children's Privacy
            </Typography>
            <Typography variant="body2" paragraph>
              Our services are not intended for children...
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              10. Changes to This Policy
            </Typography>
            <Typography variant="body2" paragraph>
              We may update this policy occasionally...
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              11. Contact Us
            </Typography>
            <Typography variant="body2" paragraph>
              If you have questions about this policy...
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
              12. Governing Law
            </Typography>
            <Typography variant="body2" paragraph>
              This Privacy Policy is governed by the laws of India.
            </Typography>
          </Box>

          {/* Footer Buttons */}
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              backgroundColor: 'inherit'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 2
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/')}
                sx={{ color: 'text.primary', borderColor: 'text.primary' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/')}
                sx={{ backgroundColor: 'text.primary', color: 'background.paper' }}
              >
                I have read and accept the terms of service
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;